'use client'

import { useState, useEffect, useRef } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { formatPrice, pluralPl } from '@/lib/format'

interface SearchResult {
  id: string
  name: string
  slug: string
  basePrice: string
  brand: { name: string } | null
  category: { name: string; slug: string; parent: { slug: string } | null } | null
  images: { url: string; alt: string | null }[]
  variants: { price: string | null }[]
}

interface Props {
  open: boolean
  onClose: () => void
}

const FOCUSABLE = 'a[href],button:not([disabled]),input,select,textarea,[tabindex]:not([tabindex="-1"])'

export function SearchModal({ open, onClose }: Props) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const inputRef = useRef<HTMLInputElement>(null)
  const modalRef = useRef<HTMLDivElement>(null)
  const itemRefs = useRef<Array<HTMLAnchorElement | null>>([])
  const previousFocusRef = useRef<Element | null>(null)
  const router = useRouter()

  function hrefFor(p: SearchResult) {
    // URL canonical = main category (parent ?? own) — M153/M155
    const catSlug = p.category?.parent?.slug ?? p.category?.slug ?? 'katalog'
    return `/katalog/${catSlug}/${p.slug}`
  }

  useEffect(() => {
    if (open) {
      previousFocusRef.current = document.activeElement
      // Reset the search state each time the modal is opened.
      /* eslint-disable react-hooks/set-state-in-effect */
      setQuery('')
      setResults([])
      /* eslint-enable react-hooks/set-state-in-effect */
      const id = setTimeout(() => inputRef.current?.focus(), 50)
      return () => clearTimeout(id)
    } else {
      if (previousFocusRef.current instanceof HTMLElement) {
        previousFocusRef.current.focus()
      }
    }
  }, [open])

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') { onClose(); return }
      if (e.key !== 'Tab' || !modalRef.current) return
      const focusable = Array.from(modalRef.current.querySelectorAll<HTMLElement>(FOCUSABLE))
      if (!focusable.length) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus() }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus() }
      }
    }
    if (open) window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [open, onClose])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (query.length < 2) { setResults([]); return }

    // Controller na poziomie efektu: cleanup abortuje trwający fetch, żeby stara
    // odpowiedź nie nadpisała wyników nowszego zapytania (race condition).
    // `cancelled` odróżnia abort z cleanupu (ignoruj, nie ruszaj stanu — loading
    // zgasi nowszy efekt) od timeoutu 5s (potraktuj jak zwykły błąd).
    const controller = new AbortController()
    let cancelled = false
    const timer = setTimeout(async () => {
      setLoading(true)
      const timeoutId = setTimeout(() => controller.abort(), 5000)
      try {
        const res = await fetch(`/api/search?q=${encodeURIComponent(query)}`, { signal: controller.signal })
        clearTimeout(timeoutId)
        if (cancelled) return
        if (!res.ok) { setResults([]); return }
        const data: unknown = await res.json()
        if (!cancelled) setResults(Array.isArray(data) ? data.slice(0, 6) : [])
      } catch {
        if (!cancelled) setResults([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }, 250)

    return () => {
      cancelled = true
      controller.abort()
      clearTimeout(timer)
    }
  }, [query])

  useEffect(() => {
    // Reset keyboard navigation whenever the result set changes.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setActiveIndex(-1)
    itemRefs.current = []
  }, [results])

  useEffect(() => {
    if (activeIndex < 0) return
    const el = itemRefs.current[activeIndex]
    el?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex])

  function handleInputKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (results.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex((i) => (i + 1 >= results.length ? 0 : i + 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex((i) => (i <= 0 ? results.length - 1 : i - 1))
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault()
      const target = results[activeIndex]
      if (target) {
        router.push(hrefFor(target))
        onClose()
      }
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (query.trim()) {
      router.push(`/szukaj?q=${encodeURIComponent(query.trim())}`)
      onClose()
    }
  }

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh] px-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-label="Wyszukiwarka produktów"
        className="w-full max-w-xl rounded-2xl overflow-hidden shadow-2xl"
        style={{ backgroundColor: '#0D1E30', border: '1px solid rgba(255,255,255,0.1)' }}
      >
        <div aria-live="polite" aria-atomic="true" className="sr-only">
          {!loading && query.length >= 2 && results.length > 0 && `${results.length} ${pluralPl(results.length, ['wynik', 'wyniki', 'wyników'])} dla ${query}`}
          {!loading && query.length >= 2 && results.length === 0 && `Brak wyników dla ${query}`}
        </div>
        {/* Input */}
        <form onSubmit={handleSubmit} className="flex items-center gap-3 px-4 py-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ color: 'rgba(255,255,255,0.35)', flexShrink: 0 }}>
            <circle cx="9" cy="9" r="6" />
            <path d="m15 15 3 3" />
          </svg>
          <input
            ref={inputRef}
            enterKeyHint="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleInputKey}
            placeholder="Szukaj sprzętu..."
            aria-label="Szukaj produktów"
            role="combobox"
            aria-autocomplete="list"
            aria-controls="search-listbox"
            aria-expanded={results.length > 0}
            aria-activedescendant={activeIndex >= 0 ? `search-option-${activeIndex}` : undefined}
            className="flex-1 text-sm bg-transparent"
            style={{ color: 'white' }}
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              aria-label="Wyczyść wyszukiwanie"
              className="w-6 h-6 flex items-center justify-center rounded-md text-base leading-none transition-colors hover:bg-white/10 hover:text-white"
              style={{ color: 'rgba(255,255,255,0.5)' }}
            >
              ×
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label="Zamknij wyszukiwarkę (Escape)"
            className="text-xs px-2 py-1 rounded transition-colors hover:bg-white/5"
            style={{ color: 'rgba(255,255,255,0.5)', border: '1px solid rgba(255,255,255,0.12)' }}
          >
            <span aria-hidden="true">Esc</span>
          </button>
        </form>

        {/* Results */}
        {query.length >= 2 && (
          <div className="max-h-[60vh] overflow-y-auto">
            {loading && (
              <div className="px-4 py-8 flex items-center justify-center gap-1.5">
                <span className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: '#1FB8B0', animationDelay: '0ms' }} />
                <span className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: '#1FB8B0', animationDelay: '150ms' }} />
                <span className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: '#1FB8B0', animationDelay: '300ms' }} />
              </div>
            )}
            {!loading && results.length === 0 && (
              <div className="px-4 py-6 text-center text-sm" style={{ color: 'rgba(255,255,255,0.55)' }}>
                Brak wyników dla „{query}&rdquo;
              </div>
            )}
            {!loading && results.length > 0 && (
              <>
                <div id="search-listbox" role="listbox" aria-label="Wyniki wyszukiwania" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                  {results.map((product, i) => {
                    const image = product.images[0]
                    const price = product.variants[0]?.price ?? product.basePrice
                    const href = hrefFor(product)
                    const isActive = i === activeIndex
                    return (
                      <Link
                        key={product.id}
                        href={href}
                        ref={(el) => { itemRefs.current[i] = el }}
                        id={`search-option-${i}`}
                        role="option"
                        aria-selected={isActive}
                        onMouseEnter={() => setActiveIndex(i)}
                        onClick={onClose}
                        className="flex items-center gap-4 px-4 py-3 transition-colors hover:bg-white/5"
                        style={{
                          borderBottom: '1px solid rgba(255,255,255,0.05)',
                          backgroundColor: isActive ? 'rgba(31,184,176,0.08)' : undefined,
                        }}
                      >
                        <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0" style={{ backgroundColor: '#0A1828' }}>
                          {image ? (
                            // eslint-disable-next-line @next/next/no-img-element
                            <img src={image.url} alt={image.alt ?? product.name} width={48} height={48} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full" style={{ backgroundColor: 'rgba(31,184,176,0.1)' }} />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          {product.brand && (
                            <p className="text-[11px] font-bold uppercase tracking-wider" style={{ color: '#1FB8B0' }}>{product.brand.name}</p>
                          )}
                          <p className="text-sm font-medium text-white truncate">{product.name}</p>
                          {product.category && (
                            <p className="text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>{product.category.name}</p>
                          )}
                        </div>
                        <p className="text-sm font-bold text-white flex-shrink-0">
                          {formatPrice(price)}
                        </p>
                      </Link>
                    )
                  })}
                </div>
                <div className="px-4 py-3">
                  <Link
                    href={`/szukaj?q=${encodeURIComponent(query.trim())}`}
                    onClick={onClose}
                    className="text-sm font-medium hover:underline"
                    style={{ color: '#FF6B47' }}
                  >
                    Pokaż wszystkie wyniki dla „{query}&rdquo; →
                  </Link>
                </div>
              </>
            )}
          </div>
        )}

        {query.length < 2 && (
          <div className="px-4 py-5 space-y-5">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: 'rgba(255,255,255,0.5)' }}>Popularne kategorie</p>
              <div className="flex flex-wrap gap-2">
                {([
                  { label: 'Snorkeling',          slug: 'snorkeling' },
                  { label: 'Freediving',           slug: 'freediving' },
                  { label: 'Nurkowanie',           slug: 'nurkowanie' },
                  { label: 'Łowiectwo podwodne',   slug: 'lowiectwo-podwodne' },
                ] as const).map(({ label, slug }) => (
                  <Link
                    key={slug}
                    href={`/katalog/${slug}`}
                    onClick={onClose}
                    className="search-pill px-3 py-1.5 rounded-full text-xs font-medium"
                  >
                    {label}
                  </Link>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: 'rgba(255,255,255,0.5)' }}>Popularne marki</p>
              <div className="flex flex-wrap gap-2">
                {([
                  { label: 'Cressi',   slug: 'cressi' },
                  { label: 'Mares',    slug: 'mares' },
                  { label: 'Aqualung', slug: 'aqualung' },
                  { label: 'Beuchat',  slug: 'beuchat' },
                  { label: 'Apeks',    slug: 'apeks' },
                ] as const).map(({ label, slug }) => (
                  <Link
                    key={slug}
                    href={`/katalog/marki/${slug}`}
                    onClick={onClose}
                    className="search-pill px-3 py-1.5 rounded-full text-xs font-medium"
                  >
                    {label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
